/**
 * tilt-pos i18n bootstrap.
 *
 * Three locales ship out of the box:
 *   en       - English (default fallback)
 *   es       - Spanish
 *   fr-CA    - French / Canada
 *
 * Detection order (i18next-browser-languagedetector default plus our
 * own ``localStorage`` slot):
 *   1. ``localStorage['tilt-pos.lang']`` - explicit user pick from
 *      the language picker in AppShell. Per-browser, per-device.
 *   2. ``navigator.language`` - browser default.
 *   3. Anchor brand_config can carry a ``default_locale`` hint that
 *      a future bootstrap step seeds into localStorage on first
 *      visit (not yet wired - keeps the door open).
 *   4. Hard fallback: en.
 *
 * Resources are imported synchronously so we don't ship a flicker
 * of untranslated copy. Bundle cost is small (~6 KB combined gzip
 * for the three JSONs); when the surface grows we can switch to
 * dynamic ``import()`` + Suspense.
 *
 * Strings are namespaced by area (login, shell, profile, home, …).
 * The admin / partner-customizable copy stays in ``brand_config``;
 * this layer only handles platform-structural strings.
 */

import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import enCommon from "./locales/en/common.json";
import esCommon from "./locales/es/common.json";
import frCACommon from "./locales/fr-CA/common.json";

export const SUPPORTED_LOCALES = ["en", "es", "fr-CA"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { common: enCommon },
      es: { common: esCommon },
      "fr-CA": { common: frCACommon },
    },
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LOCALES,
    // ``cleanCode`` normalises ``fr-ca`` → ``fr-CA`` so the picker can
    // round-trip without accidentally creating a separate detection
    // slot for casing variants.
    cleanCode: true,
    nonExplicitSupportedLngs: false,
    defaultNS: "common",
    ns: ["common"],
    interpolation: {
      escapeValue: false, // React escapes by default
    },
    detection: {
      // localStorage first - explicit user pick wins. ``cookie`` is
      // omitted because we don't want a CSRF surface; sessionStorage
      // is wrong because it forgets across tabs.
      order: ["localStorage", "navigator"],
      lookupLocalStorage: "tilt-pos.lang",
      caches: ["localStorage"],
    },
  })
  .then(() => {
    // Initial sync - ``<html lang>`` reflects the detected language.
    if (typeof document !== "undefined") {
      document.documentElement.lang = i18n.language;
    }
  });

// Keep ``<html lang>`` in sync with the active i18n language so screen
// readers pronounce content using the right voice / dictionary
// (WCAG 3.1.1 Language of Page). Inline lang on a sub-tree (3.1.2)
// is not handled here; if a partner ever ships mixed-language brand
// strings inside one document we'd add ``lang=`` per element.
i18n.on("languageChanged", (lng) => {
  if (typeof document !== "undefined") {
    document.documentElement.lang = lng;
  }
});

export default i18n;
